import { useEffect, useState } from 'react';
import { UploadCloud, Pencil, Trash2, Check, X, FileText, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { TESTIDS } from '@/constants/testIds';
import { apiAdmin } from '@/lib/api';

const COMPANY_LABEL = { smart: 'PNC Smart Solutions', unique: 'PNC Unique' };

function UploadForm({ onUploaded }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [company, setCompany] = useState('unique');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choose a PDF file to upload.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('name', name.trim());
      form.append('description', description.trim());
      form.append('company', company);
      const r = await apiAdmin.post('/admin/documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${r.data.name}" uploaded — it is now available for sending.`);
      setName(''); setDescription(''); setFile(null);
      e.target.reset?.();
      onUploaded(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form data-testid={TESTIDS.docUploadForm} onSubmit={submit} className="pnc-card rounded-lg p-7 space-y-4">
      <div>
        <p className="field-label mb-1">Upload a new document</p>
        <p className="text-xs text-[var(--muted)]">PDF only, up to 15 MB. It becomes available for sending immediately.</p>
      </div>
      <div>
        <label className="field-label block mb-1.5">Document name</label>
        <input data-testid={TESTIDS.docUploadName} value={name} onChange={(e) => setName(e.target.value)}
               className="input-bare" placeholder="e.g. Vehicle Agreement" required maxLength={200} />
      </div>
      <div>
        <label className="field-label block mb-1.5">Description (optional)</label>
        <input data-testid={TESTIDS.docUploadDescription} value={description} onChange={(e) => setDescription(e.target.value)}
               className="input-bare" placeholder="Short description shown to admins" maxLength={500} />
      </div>
      <div>
        <label className="field-label block mb-2">Company</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {['unique', 'smart'].map((c) => (
            <label key={c} className={`flex items-center gap-2.5 px-3.5 py-3 rounded-md border cursor-pointer transition-colors ${
              company === c ? 'border-[var(--silver)] bg-[#161616]' : 'border-[var(--line)] hover:border-[#4A4A4A]'}`}>
              <input type="radio" name="doc_company" value={c} checked={company === c}
                     onChange={() => setCompany(c)} className="accent-[var(--silver)]" />
              <span className="text-sm font-semibold">{COMPANY_LABEL[c]} LTD</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="field-label block mb-1.5">PDF file</label>
        <input data-testid={TESTIDS.docUploadFile} type="file" accept="application/pdf,.pdf"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="block w-full text-sm text-[var(--muted)] file:mr-3 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-[#2A2A2A] file:text-[var(--text)] file:text-xs file:font-semibold file:cursor-pointer hover:file:bg-[#3A3A3A]"
               required />
        {file && <p className="text-[11px] text-[var(--muted)] mt-1.5">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
      </div>
      <button data-testid={TESTIDS.docUploadSubmit} type="submit" disabled={uploading || !name.trim() || !file}
              className="btn-ink w-full py-3 rounded-md text-sm font-bold uppercase tracking-[0.08em] inline-flex items-center justify-center gap-2">
        <UploadCloud size={15} strokeWidth={2} /> {uploading ? 'Uploading…' : 'Upload document'}
      </button>
    </form>
  );
}

function DocRow({ doc, onChanged, onDeleteRequest }) {
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(doc.name);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const saveRename = async () => {
    if (!newName.trim() || newName.trim() === doc.name) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await apiAdmin.patch(`/admin/documents/${doc.id}`, { name: newName.trim() });
      toast.success('Document renamed.');
      onChanged(r.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setToggling(true);
    try {
      const r = await apiAdmin.patch(`/admin/documents/${doc.id}`, { active: !doc.active });
      toast.success(r.data.active ? 'Document activated.' : 'Document deactivated.');
      onChanged(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed.');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div data-testid={TESTIDS.docRow} className={`p-5 transition-colors hover:bg-[#161616] ${!doc.active ? 'opacity-55' : ''}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 mb-1 flex-wrap">
            <FileText size={14} strokeWidth={2} className="text-[var(--silver)] shrink-0" />
            {editing ? (
              <span className="inline-flex items-center gap-1.5">
                <input data-testid={TESTIDS.docRowRenameInput} value={newName} onChange={(e) => setNewName(e.target.value)}
                       className="input-bare !py-1 !px-2 text-sm w-64" maxLength={200} autoFocus
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveRename(); } if (e.key === 'Escape') setEditing(false); }} />
                <button data-testid={TESTIDS.docRowRenameSave} onClick={saveRename} disabled={saving}
                        className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10" title="Save">
                  <Check size={14} strokeWidth={2.5} />
                </button>
                <button onClick={() => { setEditing(false); setNewName(doc.name); }}
                        className="p-1.5 rounded-md text-[var(--muted)] hover:bg-[#2A2A2A]" title="Cancel">
                  <X size={14} strokeWidth={2.5} />
                </button>
              </span>
            ) : (
              <span className="font-semibold text-sm">{doc.name}</span>
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-semibold uppercase tracking-wider ${
              doc.kind === 'builtin' ? 'bg-[#2A2A2A] text-[#D4D4D4]' : 'bg-blue-500/15 text-blue-300'}`}>
              {doc.kind === 'builtin' ? <><Lock size={9} strokeWidth={2.5} /> System</> : 'Uploaded'}
            </span>
            <span className="inline-block px-2 py-0.5 text-[10px] rounded-full font-semibold uppercase tracking-wider bg-[#2A2A2A] text-[var(--silver)]">
              {COMPANY_LABEL[doc.company] || doc.company}
            </span>
            {!doc.active && (
              <span className="inline-block px-2 py-0.5 text-[10px] rounded-full font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300">Inactive</span>
            )}
          </div>
          {doc.description && <p className="text-xs text-[var(--muted)] mb-1">{doc.description}</p>}
          <p className="text-[11px] text-[#8A8A8A]">
            Added {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'} by {doc.uploaded_by || '—'}
            {doc.kind === 'uploaded' && (
              <> · <button
                data-testid="doc-row-preview"
                type="button"
                onClick={async () => {
                  try {
                    const r = await apiAdmin.get(`/admin/documents/${doc.id}/preview`, { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                    window.open(url, '_blank', 'noopener');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  } catch {
                    toast.error('Could not open the PDF preview.');
                  }
                }}
                className="underline hover:text-white"
              >Preview PDF</button></>
            )}
          </p>
        </div>
        <div className="flex gap-2 shrink-0 items-center">
          <button data-testid={TESTIDS.docRowRename} onClick={() => setEditing(true)} title="Rename"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line)] text-[var(--muted)] hover:text-white hover:border-[#4A4A4A] transition-colors">
            <Pencil size={13} strokeWidth={2} />
          </button>
          <button data-testid={TESTIDS.docRowActiveToggle} onClick={toggleActive} disabled={toggling}
                  title={doc.active ? 'Deactivate' : 'Activate'}
                  className={`px-3 h-8 rounded-md border text-xs font-semibold transition-colors ${
                    doc.active
                      ? 'border-[var(--line)] text-[var(--muted)] hover:text-amber-300 hover:border-amber-500/50'
                      : 'border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10'}`}>
            {doc.active ? 'Deactivate' : 'Activate'}
          </button>
          {doc.kind === 'uploaded' && (
            <button data-testid={TESTIDS.docRowDelete} onClick={() => onDeleteRequest(doc)} title="Delete document"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line)] text-[var(--muted)] hover:text-rose-400 hover:border-rose-500/50 hover:bg-rose-500/10 transition-colors">
              <Trash2 size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const DocumentLibrary = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiAdmin.get('/admin/documents');
      setDocs(r.data);
    } catch {
      toast.error('Failed to load the document library.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onChanged = (updated) => setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiAdmin.delete(`/admin/documents/${deleteTarget.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={TESTIDS.docLibrary} className="grid grid-cols-1 lg:grid-cols-5 gap-10">
      <section className="lg:col-span-2">
        <p className="field-label mb-3">Document Library</p>
        <h2 className="serif text-3xl sm:text-4xl leading-tight mb-2">Add a document</h2>
        <p className="text-sm text-[var(--muted)] mb-7">
          Upload any PDF and it instantly becomes available on the send page for its
          company — no developer needed. Contractors sign uploaded documents with the
          generic flow: read, enter details, draw a signature.
        </p>
        <UploadForm onUploaded={(d) => setDocs((prev) => [...prev.filter((x) => x.kind === 'builtin'), d, ...prev.filter((x) => x.kind === 'uploaded')])} />
      </section>

      <section className="lg:col-span-3">
        <p className="field-label mb-3">Manage</p>
        <h2 className="serif text-3xl sm:text-4xl leading-tight mb-3">All documents</h2>
        <div data-testid={TESTIDS.docLibraryList} className="pnc-card rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-[var(--muted)]">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--muted)]">No documents yet.</div>
          ) : (
            <div className="divide-y divide-[var(--line)] max-h-[680px] overflow-y-auto">
              {docs.map((d) => (
                <DocRow key={d.id} doc={d} onChanged={onChanged} onDeleteRequest={setDeleteTarget} />
              ))}
            </div>
          )}
        </div>
        <p className="text-[11px] text-[#8A8A8A] mt-3">
          System documents are the four built-in workflows — they can be renamed or deactivated but not deleted.
        </p>
      </section>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6">
          <div data-testid={TESTIDS.docDeleteConfirmDialog} role="dialog" aria-modal="true"
               className="bg-[#1E1E1E] border border-[var(--line)] rounded-lg shadow-2xl max-w-md w-full p-7">
            <div className="w-11 h-11 rounded-full bg-rose-500/15 text-rose-400 flex items-center justify-center mb-4">
              <AlertTriangle size={20} strokeWidth={2} />
            </div>
            <p className="serif text-2xl mb-2">Delete this document?</p>
            <p className="text-sm text-[var(--muted)] leading-relaxed mb-6">
              <strong className="text-[var(--text)]">{deleteTarget.name}</strong> will be removed from the
              library and its PDF deleted from storage. Signing links already sent for it will stop working.
              Documents that were already signed are NOT affected. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button data-testid={TESTIDS.docDeleteCancelBtn} onClick={() => setDeleteTarget(null)} disabled={deleting}
                      className="btn-ghost px-4 py-2 rounded-md text-sm font-semibold" type="button">Cancel</button>
              <button data-testid={TESTIDS.docDeleteConfirmBtn} onClick={confirmDelete} disabled={deleting}
                      className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                      type="button">{deleting ? 'Deleting…' : 'Yes, delete document'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentLibrary;
